import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

export interface EmailSender {
  readonly id: string;
  readonly name: string;
  readonly replyTo: string;
  readonly isDefault: boolean;
  readonly verifiedAt: string | null;
}

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

function failed(error: { message: string; code?: string } | null): void {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
}

/** The church's email senders, the default first. A church has a handful. */
export function useEmailSenders() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["email-senders", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<EmailSender[]> => {
      const { data, error } = await supabase
        .from("email_senders")
        .select("id, name, reply_to, is_default, verified_at")
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order("is_default", { ascending: false })
        .order("name");
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        name: row.name,
        replyTo: row.reply_to,
        isDefault: row.is_default,
        verifiedAt: row.verified_at,
      }));
    },
  });
}

function useInvalidateSenders() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["email-senders", tenantId] });
  };
}

export function useSaveEmailSender() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidateSenders();
  return useMutation({
    mutationFn: async (input: {
      readonly senderId: string | null;
      readonly name: string;
      readonly replyTo: string;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("set_email_sender", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
        p_reply_to: input.replyTo,
        ...(input.senderId === null ? {} : { p_sender_id: input.senderId }),
      });
      failed(error);
      return data as unknown as string;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteEmailSender() {
  const supabase = useSupabase();
  const invalidate = useInvalidateSenders();
  return useMutation({
    mutationFn: async (senderId: string) => {
      const { error } = await supabase.rpc("delete_email_sender", { p_sender_id: senderId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useSetDefaultEmailSender() {
  const supabase = useSupabase();
  const invalidate = useInvalidateSenders();
  return useMutation({
    mutationFn: async (senderId: string) => {
      const { error } = await supabase.rpc("set_default_email_sender", { p_sender_id: senderId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useRequestSenderVerification() {
  const supabase = useSupabase();
  const invalidate = useInvalidateSenders();
  return useMutation({
    mutationFn: async (senderId: string) => {
      const { error } = await supabase.rpc("request_email_sender_verification", {
        p_sender_id: senderId,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}
